import { useEffect, useRef, useState } from "react";
import { PencilSimple, ArrowCounterClockwise, Info } from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "components/ui/tooltip";
import {
  CurrentVacancyMetrics,
  VacancyReason,
} from "shared/utils/vacancy-metrics";

const REASON_LABEL: Record<VacancyReason, string> = {
  manual_override: "Handmatig ingesteld",
  renovation_completion: "In verhuurcyclus sinds oplevering renovatie",
  contract_end: "Sinds einde laatste contract",
  since_acquisition: "Leeg sinds aankoop",
};

const toInputValue = (d: Date | null): string => {
  if (!d || Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export interface LeegSindsCellProps {
  metrics: CurrentVacancyMetrics;
  hasOverride: boolean;
  /** Called with an ISO (yyyy-mm-dd) date, or null to clear the override. */
  onChange: (value: string | null) => Promise<void> | void;
}

export const LeegSindsCell = ({
  metrics,
  hasOverride,
  onChange,
}: LeegSindsCellProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(() => toInputValue(metrics.vacantSince));
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isEditing) {
      setDraft(toInputValue(metrics.vacantSince));
      // Defer focus until the input mounts.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isEditing, metrics.vacantSince]);

  const dayClass =
    metrics.daysVacant > 90
      ? "text-red-600"
      : metrics.daysVacant > 30
        ? "text-amber-600"
        : "text-neutral-primary";

  const handleSave = async () => {
    if (!draft) return;
    setIsSaving(true);
    try {
      await onChange(draft);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setIsSaving(true);
    try {
      await onChange(null);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex flex-col gap-1.5">
        <input
          ref={inputRef}
          type="date"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          max={toInputValue(new Date())}
          className="w-36 rounded-sm border border-neutral-primary/30 px-2 py-1 text-xs focus:border-moss-primary focus:outline-hidden"
          disabled={isSaving}
        />
        <div className="flex items-center gap-1">
          <button
            onClick={handleSave}
            disabled={isSaving || !draft}
            className="rounded-sm bg-moss-primary px-2 py-1 text-xs text-white transition-colors hover:bg-moss-primary/90 disabled:opacity-50"
          >
            {isSaving ? "…" : "Opslaan"}
          </button>
          {hasOverride && (
            <button
              onClick={handleReset}
              disabled={isSaving}
              className="flex items-center gap-1 rounded-sm border border-neutral-primary/20 px-2 py-1 text-xs text-neutral-primary hover:bg-neutral-primary/5 disabled:opacity-50"
              title="Automatische berekening gebruiken"
            >
              <ArrowCounterClockwise size={12} />
              Auto
            </button>
          )}
          <button
            onClick={() => setIsEditing(false)}
            disabled={isSaving}
            className="rounded-sm border border-neutral-primary/20 px-2 py-1 text-xs text-neutral-primary hover:bg-neutral-primary/5"
          >
            Annuleren
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-start justify-between gap-2">
      <div className="flex flex-col">
        {metrics.daysVacant > 0 ? (
          <span className={`text-sm font-medium ${dayClass}`}>
            {metrics.daysVacant} dagen leeg
          </span>
        ) : (
          <span className="text-sm text-neutral-secondary-mid">—</span>
        )}
        <span className="flex items-center gap-1 text-xs italic text-neutral-secondary-mid">
          {REASON_LABEL[metrics.reason]}
          {hasOverride && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info size={12} className="text-moss-primary" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs">
                    De datum is handmatig ingesteld en overschrijft de automatische berekening
                    (oplevering renovatie / einde laatste contract / aankoopdatum).
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
      </div>
      <button
        onClick={() => setIsEditing(true)}
        className="p-1 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-neutral-primary/5 rounded-sm"
        title="Leeg-sinds datum aanpassen"
      >
        <PencilSimple size={14} />
      </button>
    </div>
  );
};

export default LeegSindsCell;
